import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { 
  FileText, 
  Download, 
  Eye, 
  LogOut, 
  User,
  Calendar,
  Building,
  Clock,
  CheckCircle,
  AlertCircle,
  Save,
  Trash2,
  Send
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import jsPDF from "jspdf";
import AdminLayout from "@/components/AdminLayout";
import AdminDashboard from "@/components/AdminDashboard";
import DemosList from "@/components/DemosList";
import WebsiteContentEditor from "@/components/WebsiteContentEditor";
import ChatbotLeads from "@/components/ChatbotLeads";
import UserManagement from "@/components/UserManagement";
import SeoPagesEditor from "@/components/SeoPagesEditor";
import BlogEditor from "@/components/BlogEditor";
import ClientsManager from "@/components/ClientsManager";
import OnboardingDraftsAdmin from "@/components/OnboardingDraftsAdmin";
import LlmDiagnostics from "@/components/LlmDiagnostics";
import OptimizationCenter from "@/components/OptimizationCenter";
import MediaLibrary from "@/components/MediaLibrary";
import HomepageImagesAdmin from "@/components/HomepageImagesAdmin";
import BlueprintsAdmin from "@/components/BlueprintsAdmin";
import RuntimeEventsMonitor from "@/components/RuntimeEventsMonitor";
import ClientDashboard from "@/components/ClientDashboard";
import TechnicalMode from "@/components/TechnicalMode";
import GoogleConnections from "@/components/admin/GoogleConnections";
import ChatGptConnection from "@/components/admin/ChatGptConnection";
import ChatGptConsent from "@/components/admin/ChatGptConsent";
import { useAuth } from "@/contexts/AuthContext";
import { API_URL } from "../utils/api";

// Elido logo URL and base64
// Admin Cleanup 0 — projektägd asset, aldrig extern/preview-URL.
const ELIDO_LOGO_URL = "/elido-logo.png";

// Pre-encoded logo as base64 to avoid CORS issues
const ELIDO_LOGO_BASE64 = "data:image/png;base64,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";

// Generate offer number
const generateOfferNumber = () => {
  const now = new Date();
  const yy = String(now.getFullYear()).slice(-2);
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  const rand = Array.from({ length: 3 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
  return `ELIDO-${yy}${mm}${dd}-${rand}`;
};

// Format date to Swedish format
const formatDate = (date) => {
  return new Date(date).toLocaleDateString("sv-SE");
};

// Add days to date
const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("sv-SE").format(amount);
};

export const AdminPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, isAuthenticated, logout, getAuthHeaders, isAdmin } = useAuth();
  const [showPreview, setShowPreview] = useState(false);
  const [recentOffers, setRecentOffers] = useState([]);
  
  const [formData, setFormData] = useState({
    customerName: "",
    customerOrgNr: "",
    price: "",
    monthlyCost: "",
    offerDate: new Date().toISOString().split("T")[0],
    offerNumber: generateOfferNumber()
  });
  const [savedOffers, setSavedOffers] = useState([]);
  const [savingOffer, setSavingOffer] = useState(false);

  // Redirect to login if not authenticated (except the ChatGPT consent screen,
  // which manages its own unauthenticated state so the saved auth request survives).
  useEffect(() => {
    if (!loading && !isAuthenticated && !location.pathname.startsWith("/admin/chatgpt/consent")) {
      navigate("/login");
    }
  }, [loading, isAuthenticated, navigate, location.pathname]);

  // Load offers when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadSavedOffers();
    }
  }, [isAuthenticated]);

  const loadSavedOffers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/offers`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const offers = await response.json();
        setSavedOffers(offers);
      }
    } catch (error) {
      console.error("Failed to load offers:", error);
    }
  };

  const deleteOffer = async (offerNumber) => {
    if (!window.confirm(`Är du säker på att du vill radera offert ${offerNumber}?`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/offers/${encodeURIComponent(offerNumber)}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success("Offert raderad!");
        loadSavedOffers();
      } else {
        toast.error("Kunde inte radera offerten");
      }
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Kunde inte radera offerten");
    }
  };

  const saveOffer = async () => {
    if (!formData.customerName || !formData.price) {
      toast.error("Fyll i kundnamn och pris");
      return;
    }

    setSavingOffer(true);
    try {
      const response = await fetch(`${API_URL}/api/offers`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          offer_number: formData.offerNumber,
          customer_name: formData.customerName,
          customer_org_nr: formData.customerOrgNr,
          price: Number(formData.price),
          monthly_cost: Number(formData.monthlyCost),
          offer_date: formData.offerDate,
          valid_until: addDays(formData.offerDate, 14).toISOString().split("T")[0],
          created_by_name: user?.name,
          created_by_email: user?.email,
          created_by_user_id: user?.user_id,
          status: "draft"
        })
      });

      if (response.ok) {
        toast.success("Offert sparad!");
        loadSavedOffers();
        // Generate new offer number for next offer
        setFormData(prev => ({
          ...prev,
          customerName: "",
          customerOrgNr: "",
          price: "",
          offerNumber: generateOfferNumber()
        }));
        setShowPreview(false);
      } else {
        toast.error("Kunde inte spara offerten");
      }
    } catch (error) {
      console.error("Save failed:", error);
      toast.error("Kunde inte spara offerten");
    }
    setSavingOffer(false);
  };

  // Admin Cleanup 0 — sendForSignature borttagen: ingen verklig
  // signeringsåtgärd finns; knappen är dold tills en sådan byggs.

  const downloadSavedOffer = (offer) => {
    // Generate PDF for a saved offer
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let y = 20;

    // Add logo from embedded base64 (70x28)
    try {
      doc.addImage(ELIDO_LOGO_BASE64, "PNG", margin, y, 70, 28);
      y += 40;
    } catch (e) {
      console.error("Failed to add logo to PDF:", e);
      y += 10;
    }

    // Helper for centered text
    const centerText = (text, yPos, fontSize = 12) => {
      doc.setFontSize(fontSize);
      const textWidth = doc.getTextWidth(text);
      doc.text(text, (pageWidth - textWidth) / 2, yPos);
    };

    // Helper for wrapped text
    const addWrappedText = (text, x, yPos, maxWidth) => {
      const lines = doc.splitTextToSize(text, maxWidth);
      doc.text(lines, x, yPos);
      return lines.length * 6;
    };

    // Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    centerText("OFFERT – HEMSIDA", y);
    y += 20;

    // Horizontal line
    doc.setDrawColor(200);
    doc.line(margin, y, pageWidth - margin, y);
    y += 15;

    // Two column header
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("LEVERANTÖR", margin, y);
    doc.text("KUND", pageWidth / 2 + 10, y);
    y += 7;

    doc.setFont("helvetica", "normal");
    doc.text("Elido AB", margin, y);
    doc.text(offer.customer_name, pageWidth / 2 + 10, y);
    y += 5;
    doc.text("Org.nr: 559553-0873", margin, y);
    if (offer.customer_org_nr) {
      doc.text(`Org.nr: ${offer.customer_org_nr}`, pageWidth / 2 + 10, y);
    }
    y += 10;

    doc.setFont("helvetica", "bold");
    doc.text("KONTAKT", margin, y);
    doc.text("OFFERTUPPGIFTER", pageWidth / 2 + 10, y);
    y += 7;

    doc.setFont("helvetica", "normal");
    doc.text(offer.created_by_name || "Elido", margin, y);
    doc.text(`Offertnummer: ${offer.offer_number}`, pageWidth / 2 + 10, y);
    y += 5;
    doc.text(offer.created_by_email || "info@elido.nu", margin, y);
    doc.text(`Datum: ${formatDate(offer.offer_date)}`, pageWidth / 2 + 10, y);
    y += 5;
    doc.text(`Giltig till: ${formatDate(offer.valid_until)}`, pageWidth / 2 + 10, y);
    y += 15;

    // Horizontal line
    doc.line(margin, y, pageWidth - margin, y);
    y += 15;

    // Project description
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("PROJEKTBESKRIVNING", margin, y);
    y += 10;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const projectDesc = `Elido AB erbjuder design, utveckling och publicering av en modern och professionell hemsida för ${offer.customer_name}.

Målet är att skapa en tydlig och attraktiv hemsida som presenterar verksamheten på ett professionellt sätt och gör det enkelt för nya kunder att hitta information och ta kontakt.

Hemsidan utvecklas med fokus på:`;
    
    y += addWrappedText(projectDesc, margin, y, contentWidth);
    y += 5;

    const features = [
      "• Modern design",
      "• Mobilanpassning",
      "• Tydlig presentation av tjänster",
      "• Enkel kontakt för kunder",
      "• Stabil drift och säker hosting"
    ];
    
    features.forEach(feature => {
      doc.text(feature, margin + 5, y);
      y += 6;
    });
    y += 10;

    // One-time cost section
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("ENGÅNGSKOSTNAD", margin, y);
    y += 10;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("Design och utveckling av hemsida", margin, y);
    y += 7;
    
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(`${formatCurrency(offer.price)} kr`, margin, y);
    y += 10;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("Detta inkluderar:", margin, y);
    y += 6;

    const included = [
      "• Design av hemsida",
      "• Utveckling och teknisk uppsättning",
      "• Mobilanpassning (responsive design)",
      "• Kontaktformulär",
      "• Publicering på kundens domän",
      "• Grundläggande sökmotorstruktur (SEO)",
      "• Genomgång vid lansering"
    ];
    
    included.forEach(item => {
      doc.text(item, margin + 5, y);
      y += 5;
    });
    y += 10;

    // Check if we need a new page
    if (y > 240) {
      doc.addPage();
      y = 20;
    }

    // Hosting section
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("DRIFT & UNDERHÅLL", margin, y);
    y += 10;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const hostingDesc = "För att säkerställa att hemsidan alltid fungerar stabilt och säkert erbjuder Elido AB löpande drift och underhåll.";
    y += addWrappedText(hostingDesc, margin, y, contentWidth);
    y += 8;

    doc.setFont("helvetica", "bold");
    doc.text("Kostnad", margin, y);
    doc.setFont("helvetica", "normal");
    const monthlyCost = offer.monthly_cost || offer.monthly_year1 || null;
    doc.text(monthlyCost ? `${formatCurrency(monthlyCost)} kr / månad` : "Ej angivet", margin + 30, y);
    y += 10;

    doc.text("Detta inkluderar:", margin, y);
    y += 6;

    const hostingIncluded = [
      "• Webbhosting",
      "• Säkerhetsuppdateringar",
      "• Backup",
      "• Teknisk drift",
      "• Mindre innehållsändringar vid behov",
      "• Support"
    ];
    
    hostingIncluded.forEach(item => {
      doc.text(item, margin + 5, y);
      y += 5;
    });
    y += 10;

    // Payment terms
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("BETALNINGSVILLKOR", margin, y);
    y += 10;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const paymentTerms = `Engångskostnaden faktureras vid projektstart.
Månadskostnad faktureras löpande.
Betalningsvillkor: 10 dagar
Samtliga priser anges exklusive moms.`;
    y += addWrappedText(paymentTerms, margin, y, contentWidth);
    y += 15;

    // Check if we need a new page for signature
    if (y > 220) {
      doc.addPage();
      y = 20;
    }

    // Signature section
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("GODKÄNNANDE", margin, y);
    y += 15;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("Kund:", margin, y);
    y += 15;

    doc.text("Namn: _______________________________________", margin, y);
    y += 15;
    doc.text("Datum: _______________________________________", margin, y);
    y += 15;
    doc.text("Signatur: _______________________________________", margin, y);

    // Save PDF
    doc.save(`Offert_${offer.customer_name.replace(/\s+/g, "_")}_${offer.offer_number}.pdf`);
    toast.success("PDF nedladdad!");
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: "POST",
        credentials: 'include'
      });
    } catch (error) {
      console.error("Logout failed:", error);
    }
    navigate("/login");
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePreview = () => {
    if (!formData.customerName || !formData.price) {
      toast.error("Fyll i kundnamn och pris");
      return;
    }
    setShowPreview(true);
  };

  const generatePDF = () => {
    if (!formData.customerName || !formData.price) {
      toast.error("Fyll i kundnamn och pris");
      return;
    }
    if (!formData.monthlyCost) {
      toast.error("Ange månadskostnad uttryckligen — det finns inget standardpris");
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let y = 20;

    // Add logo from embedded base64 (70x28)
    try {
      doc.addImage(ELIDO_LOGO_BASE64, "PNG", margin, y, 70, 28);
      y += 40;
    } catch (e) {
      console.error("Failed to add logo to PDF:", e);
      y += 10;
    }

    // Helper for centered text
    const centerText = (text, yPos, fontSize = 12) => {
      doc.setFontSize(fontSize);
      const textWidth = doc.getTextWidth(text);
      doc.text(text, (pageWidth - textWidth) / 2, yPos);
    };

    // Helper for wrapped text
    const addWrappedText = (text, x, yPos, maxWidth) => {
      const lines = doc.splitTextToSize(text, maxWidth);
      doc.text(lines, x, yPos);
      return lines.length * 6;
    };

    // Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    centerText("OFFERT – HEMSIDA", y);
    y += 20;

    // Horizontal line
    doc.setDrawColor(200);
    doc.line(margin, y, pageWidth - margin, y);
    y += 15;

    // Two column header
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("LEVERANTÖR", margin, y);
    doc.text("KUND", pageWidth / 2 + 10, y);
    y += 7;

    doc.setFont("helvetica", "normal");
    doc.text("Elido AB", margin, y);
    doc.text(formData.customerName, pageWidth / 2 + 10, y);
    y += 5;
    doc.text("Org.nr: 559553-0873", margin, y);
    if (formData.customerOrgNr) {
      doc.text(`Org.nr: ${formData.customerOrgNr}`, pageWidth / 2 + 10, y);
    }
    y += 10;

    doc.setFont("helvetica", "bold");
    doc.text("KONTAKT", margin, y);
    doc.text("OFFERTUPPGIFTER", pageWidth / 2 + 10, y);
    y += 7;

    doc.setFont("helvetica", "normal");
    doc.text(user?.fullName || "Elido", margin, y);
    doc.text(`Offertnummer: ${formData.offerNumber}`, pageWidth / 2 + 10, y);
    y += 5;
    doc.text(user?.displayEmail || "info@elido.nu", margin, y);
    doc.text(`Datum: ${formatDate(formData.offerDate)}`, pageWidth / 2 + 10, y);
    y += 5;
    doc.text(`Giltig till: ${formatDate(addDays(formData.offerDate, 14))}`, pageWidth / 2 + 10, y);
    y += 15;

    // Horizontal line
    doc.line(margin, y, pageWidth - margin, y);
    y += 15;

    // Project description
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("PROJEKTBESKRIVNING", margin, y);
    y += 10;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const projectDesc = `Elido AB erbjuder design, utveckling och publicering av en modern och professionell hemsida för ${formData.customerName}.

Målet är att skapa en tydlig och attraktiv hemsida som presenterar verksamheten på ett professionellt sätt och gör det enkelt för nya kunder att hitta information och ta kontakt.

Hemsidan utvecklas med fokus på:`;
    
    y += addWrappedText(projectDesc, margin, y, contentWidth);
    y += 5;

    const features = [
      "• Modern design",
      "• Mobilanpassning",
      "• Tydlig presentation av tjänster",
      "• Enkel kontakt för kunder",
      "• Stabil drift och säker hosting"
    ];
    
    features.forEach(feature => {
      doc.text(feature, margin + 5, y);
      y += 6;
    });
    y += 10;

    // One-time cost section
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("ENGÅNGSKOSTNAD", margin, y);
    y += 10;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("Design och utveckling av hemsida", margin, y);
    y += 7;
    
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(`${formatCurrency(formData.price)} kr`, margin, y);
    y += 10;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("Detta inkluderar:", margin, y);
    y += 6;

    const included = [
      "• Design av hemsida",
      "• Utveckling och teknisk uppsättning",
      "• Mobilanpassning (responsive design)",
      "• Kontaktformulär",
      "• Publicering på kundens domän",
      "• Grundläggande sökmotorstruktur (SEO)",
      "• Genomgång vid lansering"
    ];
    
    included.forEach(item => {
      doc.text(item, margin + 5, y);
      y += 5;
    });
    y += 10;

    // Check if we need a new page
    if (y > 240) {
      doc.addPage();
      y = 20;
    }

    // Hosting section
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("DRIFT & UNDERHÅLL", margin, y);
    y += 10;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const hostingDesc = "För att säkerställa att hemsidan alltid fungerar stabilt och säkert erbjuder Elido AB löpande drift och underhåll.";
    y += addWrappedText(hostingDesc, margin, y, contentWidth);
    y += 8;

    doc.setFont("helvetica", "bold");
    doc.text("Kostnad", margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(`${formatCurrency(formData.monthlyCost)} kr / månad`, margin + 30, y);
    y += 10;

    doc.text("Detta inkluderar:", margin, y);
    y += 6;

    const hostingIncluded = [
      "• Webbhosting",
      "• Säkerhetsuppdateringar",
      "• Backup",
      "• Teknisk drift",
      "• Mindre innehållsändringar vid behov",
      "• Support"
    ];
    
    hostingIncluded.forEach(item => {
      doc.text(item, margin + 5, y);
      y += 5;
    });
    y += 10;

    // Payment terms
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("BETALNINGSVILLKOR", margin, y);
    y += 10;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const paymentTerms = `Engångskostnaden faktureras vid projektstart.
Månadskostnad faktureras löpande.
Betalningsvillkor: 10 dagar
Samtliga priser anges exklusive moms.`;
    y += addWrappedText(paymentTerms, margin, y, contentWidth);
    y += 15;

    // Check if we need a new page for signature
    if (y > 220) {
      doc.addPage();
      y = 20;
    }

    // Signature section
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("GODKÄNNANDE", margin, y);
    y += 15;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("Kund:", margin, y);
    y += 15;

    doc.text("Namn: _______________________________________", margin, y);
    y += 15;
    doc.text("Datum: _______________________________________", margin, y);
    y += 15;
    doc.text("Signatur: _______________________________________", margin, y);

    // Save PDF
    doc.save(`Offert_${formData.customerName.replace(/\s+/g, "_")}_${formData.offerNumber}.pdf`);

    // Add to recent offers
    const newOffer = {
      offerNumber: formData.offerNumber,
      customerName: formData.customerName,
      date: formData.offerDate,
      price: formData.price
    };
    setRecentOffers(prev => [newOffer, ...prev.slice(0, 9)]);

    // Generate new offer number for next offer
    setFormData(prev => ({
      ...prev,
      offerNumber: generateOfferNumber()
    }));

    toast.success("PDF skapad och nedladdad!");
  };

  const hasProcessedRef = useRef(false);

  // Auth callback handling
  useEffect(() => {
    const processSessionId = async (sessionId) => {
      if (hasProcessedRef.current) return;
      hasProcessedRef.current = true;

      try {
        const response = await fetch(`${API_URL}/api/auth/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId })
        });

        if (!response.ok) {
          throw new Error("Session exchange failed");
        }

        // Clear the hash and reload auth state
        window.history.replaceState(null, "", "/admin");
        window.location.reload();
      } catch (error) {
        console.error("Session processing error:", error);
        toast.error("Autentisering misslyckades");
      }
    };

    const hash = window.location.hash;
    if (hash.includes("session_id=")) {
      const sessionId = hash.split("session_id=")[1]?.split("&")[0];
      if (sessionId) {
        processSessionId(sessionId);
      }
    }
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Kontrollerar behörighet...</p>
        </div>
      </div>
    );
  }

  // ChatGPT OAuth consent screen — renders standalone and handles its own auth
  // (so an admin can log in and return to the saved authorization request).
  if (location.pathname.startsWith("/admin/chatgpt/consent")) {
    return <ChatGptConsent />;
  }

  // Not authenticated - will redirect via useEffect
  if (!isAuthenticated) {
    return null;
  }

  // Authenticated - Admin interface
  const renderContent = () => {
    const path = location.pathname;
    
    if (path === "/admin/demos") {
      return <DemosList />;
    }
    
    if (path === "/admin/website") {
      return <WebsiteContentEditor />;
    }
    
    if (path === "/admin/leads") {
      return <ChatbotLeads />;
    }
    
    if (path === "/admin/users" && isAdmin()) {
      return <UserManagement />;
    }
    
    if (path === "/admin/seo-pages" && isAdmin()) {
      return <SeoPagesEditor />;
    }
    
    if (path === "/admin/blog" && isAdmin()) {
      return <BlogEditor />;
    }
    
    if (path === "/admin/handymen" && isAdmin()) {
      // Legacy-route → enda kundarbetsytan är /admin/clients.
      return <Navigate to="/admin/clients" replace />;
    }

    if (path === "/admin/clients" && isAdmin()) {
      return <ClientsManager />;
    }

    // H3.2 — Kund 360 per-client dashboard
    if (path.startsWith("/admin/clients/") && isAdmin()) {
      return <ClientDashboard />;
    }

    if (path === "/admin/onboarding-drafts" && isAdmin()) {
      return <OnboardingDraftsAdmin />;
    }

    if (path === "/admin/google-kopplingar" && isAdmin()) {
      return <GoogleConnections />;
    }

    if (path === "/admin/chatgpt" && isAdmin()) {
      return <ChatGptConnection />;
    }

    if (path === "/admin/optimering" && isAdmin()) {
      return <OptimizationCenter />;
    }

    if (path === "/admin/llm-diagnostik" && isAdmin()) {
      return <LlmDiagnostics />;
    }

    if (path === "/admin/blueprints" && isAdmin()) {
      return <BlueprintsAdmin />;
    }
    // H3.2.3 — deep-link from Kund 360 / specialist views into a
    // specific client's blueprint detail page.
    if (path.startsWith("/admin/blueprints/") && isAdmin()) {
      return <BlueprintsAdmin />;
    }

    if (path === "/admin/runtime-events" && isAdmin()) {
      return <RuntimeEventsMonitor />;
    }

    // Admin Cleanup 0 — System → Tekniskt läge
    if (path === "/admin/system/teknik" && isAdmin()) {
      return <TechnicalMode />;
    }
    
    if (path === "/admin/media" && isAdmin()) {
      return <MediaLibrary />;
    }
    
    if (path === "/admin/homepage-images" && isAdmin()) {
      return <HomepageImagesAdmin />;
    }
    
    if (path === "/admin/offers") {
      return (
        <>
          {/* Form Section */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <div className="flex items-center gap-3 mb-8">
              <FileText className="w-6 h-6 text-gray-400" />
              <h2 className="text-lg font-semibold text-gray-900">Offertgenerator</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kundnamn *
                </label>
                <input
                  type="text"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleFormChange}
                  placeholder="Företagsnamn AB"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-400 transition-colors"
                  data-testid="customer-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kundens org.nr
                </label>
                <input
                  type="text"
                  name="customerOrgNr"
                  value={formData.customerOrgNr}
                  onChange={handleFormChange}
                  placeholder="559XXX-XXXX"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-400 transition-colors"
                  data-testid="customer-org-nr-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pris exkl. moms (kr) *
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleFormChange}
                  placeholder="6990"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-400 transition-colors"
                  data-testid="price-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Månadskostnad (kr)
                </label>
                <input
                  type="number"
                  name="monthlyCost"
                  value={formData.monthlyCost}
                  onChange={handleFormChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-400 transition-colors"
                  data-testid="monthly-cost-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Offertdatum
                </label>
                <input
                  type="date"
                  name="offerDate"
                  value={formData.offerDate}
                  onChange={handleFormChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-400 transition-colors"
                  data-testid="offer-date-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Offertnummer
                </label>
                <input
                  type="text"
                  value={formData.offerNumber}
                  readOnly
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                  data-testid="offer-number-display"
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-wrap gap-4 mt-8">
              <button
                onClick={handlePreview}
                className="flex items-center gap-2 px-6 py-3 border border-gray-200 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                data-testid="preview-button"
              >
                <Eye className="w-5 h-5" />
                Förhandsgranska
              </button>
              <button
                onClick={generatePDF}
                className="flex items-center gap-2 px-6 py-3 border border-gray-200 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                data-testid="download-pdf-button"
              >
                <Download className="w-5 h-5" />
                Ladda ner PDF
              </button>
              <button
                onClick={saveOffer}
                disabled={savingOffer}
                className="flex items-center gap-2 px-6 py-3 border border-gray-200 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                data-testid="save-offer-button"
              >
                <Save className="w-5 h-5" />
                {savingOffer ? "Sparar..." : "Spara utkast"}
              </button>
              {/* Admin Cleanup 0 — "Skicka för signering" dold: ingen
                  faktisk signerings-/utskicksåtgärd finns ännu. */}
            </div>
          </div>

          {/* Preview Section */}
          {showPreview && formData.customerName && formData.price && (
            <div className="bg-white rounded-lg shadow-sm p-8 mb-8" data-testid="offer-preview">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Förhandsgranskning</h3>
                <button
                  onClick={() => setShowPreview(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="border border-gray-200 rounded-lg p-8 bg-gray-50">
                {/* Logo */}
                <div className="mb-8">
                  <img src={ELIDO_LOGO_URL} alt="Elido" className="h-16" />
                </div>

                {/* Preview Header */}
                <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">
                  OFFERT – HEMSIDA
                </h2>

                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-500 uppercase mb-2">Leverantör</h4>
                    <p className="font-medium">Elido AB</p>
                    <p className="text-gray-600">Org.nr: 559553-0873</p>
                    <div className="mt-4">
                      <h4 className="text-sm font-semibold text-gray-500 uppercase mb-2">Kontakt</h4>
                      <p className="font-medium">{user?.fullName}</p>
                      <p className="text-gray-600">{user?.displayEmail}</p>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-500 uppercase mb-2">Kund</h4>
                    <p className="font-medium">{formData.customerName}</p>
                    {formData.customerOrgNr && (
                      <p className="text-gray-600">Org.nr: {formData.customerOrgNr}</p>
                    )}
                    <div className="mt-4">
                      <p className="text-sm"><span className="text-gray-500">Offertnummer:</span> {formData.offerNumber}</p>
                      <p className="text-sm"><span className="text-gray-500">Datum:</span> {formatDate(formData.offerDate)}</p>
                      <p className="text-sm"><span className="text-gray-500">Giltig till:</span> {formatDate(addDays(formData.offerDate, 14))}</p>
                    </div>
                  </div>
                </div>

                <hr className="my-6" />

                {/* Project Description */}
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Projektbeskrivning</h4>
                  <p className="text-gray-600 mb-4">
                    Elido AB erbjuder design, utveckling och publicering av en modern och professionell hemsida för {formData.customerName}.
                  </p>
                  <p className="text-gray-600 mb-4">
                    Målet är att skapa en tydlig och attraktiv hemsida som presenterar verksamheten på ett professionellt sätt och gör det enkelt för nya kunder att hitta information och ta kontakt.
                  </p>
                </div>

                {/* One-time cost */}
                <div className="mb-6 bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Engångskostnad</h4>
                  <p className="text-gray-600 mb-2">Design och utveckling av hemsida</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(formData.price)} kr</p>
                </div>

                {/* Monthly cost */}
                <div className="mb-6 bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Drift & Underhåll</h4>
                  <div>
                    <p className="text-sm text-gray-500">Kostnad</p>
                    <p className="font-semibold">{formatCurrency(formData.monthlyCost)} kr/mån</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Saved Offers from Database */}
          {savedOffers.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-8 mb-8" data-testid="saved-offers">
              <div className="flex items-center gap-4 mb-6">
                <img src={ELIDO_LOGO_URL} alt="Elido" className="h-12" />
                <h3 className="text-lg font-semibold text-gray-900">Sparade offerter</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Offertnummer</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Kundnamn</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Org.nr</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Datum</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Pris</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Åtgärder</th>
                    </tr>
                  </thead>
                  <tbody>
                    {savedOffers.map((offer, i) => (
                      <tr key={i} className="border-b border-gray-100 last:border-b-0">
                        <td className="py-3 px-4 font-mono text-sm">{offer.offer_number}</td>
                        <td className="py-3 px-4">{offer.customer_name}</td>
                        <td className="py-3 px-4 text-gray-500">{offer.customer_org_nr || "-"}</td>
                        <td className="py-3 px-4">
                          {offer.status === "pending_signature" ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                              <Clock className="w-3 h-3" />
                              Väntar på signering
                            </span>
                          ) : offer.status === "signed" ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                              <CheckCircle className="w-3 h-3" />
                              Signerad
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                              Utkast
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-500">{formatDate(offer.offer_date)}</td>
                        <td className="py-3 px-4 text-right">{formatCurrency(offer.price)} kr</td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => downloadSavedOffer(offer)}
                              className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                              title="Ladda ner PDF"
                              data-testid={`download-offer-${offer.offer_number}`}
                            >
                              <Download className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => deleteOffer(offer.offer_number)}
                              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                              title="Radera offert"
                              data-testid={`delete-offer-${offer.offer_number}`}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Recent Offers (Session) */}
          {recentOffers.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-8" data-testid="recent-offers">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Senaste offerter (denna session)</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Offertnummer</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Kundnamn</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Datum</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Pris</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentOffers.map((offer, i) => (
                      <tr key={i} className="border-b border-gray-100 last:border-b-0">
                        <td className="py-3 px-4 font-mono text-sm">{offer.offerNumber}</td>
                        <td className="py-3 px-4">{offer.customerName}</td>
                        <td className="py-3 px-4 text-gray-500">{formatDate(offer.date)}</td>
                        <td className="py-3 px-4 text-right">{formatCurrency(offer.price)} kr</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      );
    }
    
    // Default - Dashboard
    return <AdminDashboard user={user} />;
  };

  return (
    <div data-testid="admin-page">
      <Toaster position="top-center" richColors />
      <AdminLayout user={user} onLogout={handleLogout}>
        {renderContent()}
      </AdminLayout>
    </div>
  );
};

export default AdminPage;
